// Pure maths for the Matches widget (no DOM/React): turn a player's raw shot +
// pass events into per-match incident markers, and turn a match + the player's
// team into an opponent + home/away label. Kept apart from the React view so the
// classification (which live in opaque event jsonb) is decided in one tested place.
//
// Data facts (verified against live payloads, 2026-09-08):
//  - Goal   = a shot whose rawEventAttributes.shot.outcome.name === "Goal"
//             (reuse v1's outcomeName — a keeper).
//  - KeyPass= a pass with rawEventAttributes.pass.shot_assist === true.
//  - Assist = a pass with rawEventAttributes.pass.goal_assist === true.
//  - Shot   = every shot event.
// Markers use the event's REAL match minute (no period cap) so a 94' goal sits at
// 94 on the same real-minute axis the v2 Matches bars now use; the axis is sized
// to fit (appearancesTimeline.axisMaxForValues).

import type {
  PlayerShot,
  PlayerEvent,
  MatchSummary,
  MatchGoal,
  PlayerAppearance,
} from "@catbento/shared";
import { outcomeName, shotXg } from "../widgets/shotMap.js";
import type { TimelineRow } from "../widgets/appearancesTimeline.js";

/** Displayed match minute — rounded, matching Ben's prototype (a 95.5' change
 *  reads "96'"). Positions on the axis stay fractional; only labels round. */
function mmin(minute: number): string {
  return `${Math.round(minute)}'`;
}

/** The pass recipient's name from a pass event's jsonb (`pass.recipient.name`),
 *  or null when absent — for the Key Pass / Assist tooltips. */
function passRecipient(pa: Record<string, unknown>): string | null {
  const r = pa.recipient;
  if (r && typeof r === "object") {
    const name = (r as Record<string, unknown>).name;
    if (typeof name === "string") return name;
  }
  return null;
}

// "teamGoal" and "positionChange" are toggled like the other overlays but their
// markers come from separate feeds (/match-goals; the appearance positionDurations),
// so they render via TeamGoalMarker / PositionChangeMarker (below), not Incident.
export type IncidentKind =
  | "shot"
  | "goal"
  | "keyPass"
  | "assist"
  | "teamGoal"
  | "positionChange";

export interface Incident {
  matchId: number;
  /** Real match minute (axis coordinate — fractional; the label rounds). */
  minute: number;
  kind: IncidentKind;
  /** Hover text (full parity with the prototype): a shot shows outcome + xG, a
   *  key pass / assist shows the recipient. May be multi-line (`\n`). */
  tooltip: string;
}

/** Read `rawEventAttributes.pass` off a pass event (opaque jsonb), or null. */
function passAttrs(p: PlayerEvent): Record<string, unknown> | null {
  const raw = p.rawEventAttributes;
  if (typeof raw !== "object" || raw === null) return null;
  const pass = (raw as Record<string, unknown>).pass;
  return typeof pass === "object" && pass !== null ? (pass as Record<string, unknown>) : null;
}

/**
 * Flatten a player's shots + passes into incident markers. One shot yields a
 * `shot` (and a `goal` too when it went in); one pass yields a `keyPass` and/or
 * `assist` when flagged. Pure.
 */
export function buildIncidents(shots: PlayerShot[], passes: PlayerEvent[]): Incident[] {
  const out: Incident[] = [];
  for (const s of shots) {
    const minute = s.minute ?? 0;
    // Shot tooltip (prototype parity): "Saved · 45'\nxG 0.42". The goal marker
    // reuses it (a goal is just a shot whose outcome is "Goal").
    const xg = shotXg(s);
    const tip = `${outcomeName(s) ?? "Shot"} · ${mmin(minute)}${xg !== null ? `\nxG ${xg.toFixed(2)}` : ""}`;
    out.push({ matchId: s.matchId, minute, kind: "shot", tooltip: tip });
    if (outcomeName(s) === "Goal") out.push({ matchId: s.matchId, minute, kind: "goal", tooltip: tip });
  }
  for (const p of passes) {
    const pa = passAttrs(p);
    if (!pa) continue;
    const minute = p.minute ?? 0;
    const to = passRecipient(pa);
    const suffix = `${to ? ` to ${to}` : ""} · ${mmin(minute)}`;
    if (pa.shot_assist === true)
      out.push({ matchId: p.matchId, minute, kind: "keyPass", tooltip: `Key Pass${suffix}` });
    if (pa.goal_assist === true)
      out.push({ matchId: p.matchId, minute, kind: "assist", tooltip: `Goal Assist${suffix}` });
  }
  return out;
}

/** Group incidents by matchId, keeping only the currently-active overlay kinds. */
export function incidentsByMatch(
  incidents: Incident[],
  active: ReadonlySet<IncidentKind>
): Map<number, Incident[]> {
  const byMatch = new Map<number, Incident[]>();
  for (const inc of incidents) {
    if (!active.has(inc.kind)) continue;
    const list = byMatch.get(inc.matchId);
    if (list) list.push(inc);
    else byMatch.set(inc.matchId, [inc]);
  }
  return byMatch;
}

/** A goal marker for the Team Goals overlay: one dot per goal in the match, with
 *  the scorer/team for the tooltip and a for/against flag for its colour. */
export interface TeamGoalMarker {
  matchId: number;
  /** Real match minute (axis coordinate — fractional; the label rounds). */
  minute: number;
  isPlayerTeam: boolean;
  /** Hover text: "A. Striker (Arsenal WFC) (Pen) · 67'". */
  tooltip: string;
}

/**
 * Group every match goal by matchId, at its real match minute (a 94' goal sits at
 * 94 on the extended axis). Goals with no minute are dropped — they can't be
 * placed on the axis. Pure.
 */
export function teamGoalsByMatch(goals: MatchGoal[]): Map<number, TeamGoalMarker[]> {
  const byMatch = new Map<number, TeamGoalMarker[]>();
  for (const g of goals) {
    if (g.minute === null) continue;
    const team = g.teamName ? ` (${g.teamName})` : "";
    const pen = g.isPenalty ? " (Pen)" : "";
    const marker: TeamGoalMarker = {
      matchId: g.matchId,
      minute: g.minute,
      isPlayerTeam: g.isPlayerTeam,
      tooltip: `${g.scorerName ?? "Goal"}${team}${pen} · ${mmin(g.minute)}`,
    };
    const list = byMatch.get(g.matchId);
    if (list) list.push(marker);
    else byMatch.set(g.matchId, [marker]);
  }
  return byMatch;
}

/** A position-change marker: the player moved to a new position at this real
 *  match minute. One per change (an appearance with N positions yields N−1). */
export interface PositionChangeMarker {
  matchId: number;
  /** Real match minute of the change = onPitchMinute + Σ durations up to it
   *  (axis coordinate — fractional; the label rounds). */
  minute: number;
  /** The position moved TO (a code string like "LWB"). */
  toPosition: string;
  /** Hover text: "Moved to LWB · 96'". */
  tooltip: string;
}

/** Read `positionDurations` off an appearance (opaque jsonb) as an ordered list
 *  of `{ position, duration }`, or [] if the shape isn't what we expect. */
function positionSegments(a: PlayerAppearance): { position: string; duration: number }[] {
  const raw = a.positionDurations;
  if (!Array.isArray(raw)) return [];
  const out: { position: string; duration: number }[] = [];
  for (const seg of raw) {
    if (!seg || typeof seg !== "object") continue;
    const s = seg as Record<string, unknown>;
    if (typeof s.position === "string" && typeof s.duration === "number") {
      out.push({ position: s.position, duration: s.duration });
    }
  }
  return out;
}

/**
 * Position-change markers per match. An appearance's `positionDurations` is an
 * ordered list of `{ position, duration }`; a player who filled ≥2 positions
 * changed at each boundary. The change minute = the on-pitch minute plus the
 * running sum of durations up to that boundary (verified in the prototype: it
 * lands within rounding of the real off-pitch minute). One appearance with N
 * positions yields N−1 change markers; ≤1 position yields none. Pure.
 */
export function buildPositionChanges(
  appearances: PlayerAppearance[]
): Map<number, PositionChangeMarker[]> {
  const byMatch = new Map<number, PositionChangeMarker[]>();
  for (const a of appearances) {
    const segs = positionSegments(a);
    if (segs.length < 2) continue;
    let cursor = a.onPitchMinute ?? 0;
    const markers: PositionChangeMarker[] = [];
    for (let i = 0; i < segs.length - 1; i++) {
      cursor += segs[i]!.duration;
      const toPosition = segs[i + 1]!.position;
      markers.push({
        matchId: a.matchId,
        minute: cursor,
        toPosition,
        tooltip: `Moved to ${toPosition} · ${mmin(cursor)}`,
      });
    }
    byMatch.set(a.matchId, markers);
  }
  return byMatch;
}

export interface MatchLabel {
  /** The opponent team name, or a fallback (the date) when it can't be resolved. */
  text: string;
  /** "H"/"A" when resolved from the player's team, else null. */
  homeAway: "H" | "A" | null;
}

/**
 * Opponent + home/away for one match, from the fixture (`/matches`) and the
 * player's team id IN THAT MATCH (`/player-appearances.teamId`). Matched by id —
 * not by the player's *current* team name, which is wrong for a past season after
 * a transfer (e.g. McCabe's 25/26 Arsenal games would otherwise all fail to match
 * "Chelsea" and, worse, her Arsenal-vs-Chelsea games would mislabel the opponent
 * as Arsenal). Falls back to the match date when the fixture or team id is missing
 * — honest, never a wrong opponent. Pure.
 */
export function matchLabel(
  match: MatchSummary | undefined,
  playerTeamId: number | null,
  dateFallback: string
): MatchLabel {
  if (match && playerTeamId !== null) {
    if (playerTeamId === match.homeTeamId) return { text: match.awayTeamName, homeAway: "H" };
    if (playerTeamId === match.awayTeamId) return { text: match.homeTeamName, homeAway: "A" };
  }
  return { text: dateFallback, homeAway: null };
}

/**
 * Multi-line hover text for a match BAR (prototype parity): opponent (H/A),
 * the score + result (W/L/D), and how the player featured (started / sub on /
 * off). The score line is shown only when we know the player's side and both
 * scores — otherwise (a date-fallback row) just the opponent/date + role. Pure.
 */
export function matchBarTooltip(
  row: TimelineRow,
  label: MatchLabel,
  match: MatchSummary | undefined
): string {
  const lines: string[] = [];
  lines.push(label.homeAway ? `${label.text} (${label.homeAway})` : label.text);

  if (match && label.homeAway && match.homeScore !== null && match.awayScore !== null) {
    const my = label.homeAway === "H" ? match.homeScore : match.awayScore;
    const opp = label.homeAway === "H" ? match.awayScore : match.homeScore;
    const result = my > opp ? "W" : my < opp ? "L" : "D";
    lines.push(`${my}–${opp} (${result})`); // en-dash, e.g. "2–1 (W)"
  }

  if (row.played) {
    const start = row.started ? "Started" : `On as sub (${Math.round(row.on)}')`;
    const end = row.exit === "sub" ? `Off ${Math.round(row.off)}'` : "Played to full time";
    lines.push(`${row.minutes} mins — ${start}, ${end}`);
  } else {
    lines.push("In squad, no minutes");
  }
  return lines.join("\n");
}
